import AbstractApiService from 'src/services/abstractApiService';
import { api } from 'pages/APIs';
import {
  webhookNewExportConfiguration,
  webhookEditExportConfiguration,
  Flow2NewExportConfiguration,
  Flow2EditExportConfiguration,
  SFTPExportConfiguration,
  InforSFTPExportConfiguration,
  SMBExportConfiguration,
} from 'src/types';
import type { dynamicObj } from 'src/pages/assets';

/**
 * Response type for WatchDog service status endpoint
 */
export interface WatchdogStatusResponse {
  success: boolean;
  online: boolean;
  last_seen: string | null;
  last_restart: string | null;
  has_pending_commands: boolean;
  version?: string;                        // Current installed version
  version_first_seen?: string | null;      // ISO timestamp when version first seen
  auto_update_enabled?: boolean | null;    // Auto-update setting
  auto_update_check_interval?: number;     // Update check interval in seconds
  latest_version?: string | null;          // Latest version from GitHub
  latest_version_checked_at?: string | null; // When latest version was checked
  installation_path?: string | null;       // Installation directory path
  free_ram_bytes?: number | null;          // Available RAM in bytes
  free_disk_bytes?: number | null;         // Free disk space in bytes
  message?: string;
}

/**
 * Response type for WatchDog command creation endpoint
 */
export interface CreateCommandResponse {
  success: boolean;
  id?: string;
  command_type?: string;
  status?: string;
  created_at?: string;
  message?: string;
}

export default class ExportApiService extends AbstractApiService {
  public constructor() {
    super({});
  }

  public async createOrModifyWatchdogConfig(orgId: string, data: string): Promise<any> {
    const formData = new FormData();
    formData.append('org_id', orgId);
    formData.append('config', data);

    return super.post(api.create_or_update_watchdog_config, formData);
  }

  public async getWatchdogConfig(orgId: string): Promise<any> {
    return super.get(api.get_watchdog_config + '/' + orgId);
  }

  /**
   * Get the current status of the WatchDog service for this organization.
   * Returns online/offline status and last heartbeat timestamp.
   */
  public async getWatchdogStatus(): Promise<WatchdogStatusResponse> {
    return super.get(api.watchdog_status);
  }

  /**
   * Create a command for the WatchDog service.
   * Commands are queued and polled by the WatchDog service.
   * @param commandType - RESTART, UPDATE, or SET_AUTO_UPDATE
   * @param data - Optional data payload for the command (e.g., { enabled: true } for SET_AUTO_UPDATE)
   */
  public async createWatchdogCommand(
    commandType: string,
    data?: Record<string, unknown>,
  ): Promise<CreateCommandResponse> {
    const formData = new FormData();
    formData.append('command_type', commandType);
    if (data) {
      formData.append('payload', JSON.stringify(data));
    }
    return super.post(api.watchdog_commands, formData);
  }

  public async getAll(): Promise<any> {
    return super.post(api.get_export_configurations, new FormData());
  }

  public async getExportConfigurationMapping(id: string): Promise<any> {
    return super.get(api.export_configuration_mapping + id);
  }

  public async updateTomlConfig(
    id: string,
    mappingValue: string,
  ): Promise<any> {
    const formData = new FormData();
    formData.append('mapping_value', mappingValue);
    const url = api.export_configuration_mapping + id;
    return super.put(api.export_configuration_mapping + id, formData);
  }

  public async delete(id: string): Promise<any> {
    const formData = new FormData();
    formData.append('id', id);
    return super.post(api.delete_existing_export_configuration, formData);
  }

  public async updateActive(id: string, is_active: boolean): Promise<any> {
    const formData = new FormData();
    formData.append('id', id);
    formData.append('is_active', is_active === true ? 'true' : 'false');
    return super.post(api.update_configuration_visibility, formData);
  }

  // public async updateTomlConfig(id: string, mappingValue: string): Promise<any> {
  //   const url = api.export_configuration_mapping + id + '?org_id='+ this.getOrgId() + '&mapping_value=' + encodeURIComponent(mappingValue) ;
  //   return super.put(url, {});
  // }

  public async getTomlFile(): Promise<any> {
    return super.get(api.get_toml_file);
  }

  public async deleteRecord(id: string): Promise<any> {
    const formData = new FormData();
    formData.append('id', id);
    return super.post(api.delete_existing_export_configuration, formData);
  }

  public saveWebhookConfig(
    configuration:
      | webhookNewExportConfiguration
      | webhookEditExportConfiguration,
  ): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(configuration)) {
      formData.append(key, value);
    }
    return super.post(api.define_webhook_export, formData);
  }

  public saveInforConfig(configuration: any): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(configuration)) {
      formData.append(key, value as string);
    }
    return super.post(api.define_infor_export, formData);
  }

  public saveVertexConfig(configuration: any): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(configuration)) {
      formData.append(key, value as string);
    }
    return super.post(api.define_vertex_export, formData);
  }

  public saveGenericApiConfig(configuration: any): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(configuration)) {
      formData.append(key, value as string);
    }
    return super.post(api.define_generic_api_export, formData);
  }

  public getMappingFile(id: number, config_type: string): Promise<any> {
    const data = { type: config_type };
    const querystring = this.encodeQueryData(data);
    return super.get(api.configurations_download + id + '?' + querystring, {
      responseType: 'blob',
    });
  }

  async getFlowToken(configuration: any): Promise<any> {
    const data = {
      username: configuration.username,
      password: configuration.password,
    };
    const url =
      configuration.export_url.replace(/\/+$/, '') +
      '/api/v4/auth/token/obtain/';

    const options = {
      error: this.handleFlowTokenError.bind(this),
    };

    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, String(value));
    }

    return super.post(url, formData, options);
  }

  protected handleFlowTokenError(error: unknown): any {
    if (!(error instanceof Error)) {
      super.handleError(error);
    }
    if (error instanceof Error && error.message == 'Network Error') {
      throw new Error('Invalid url, Please check.');
    }

    if (
      this.isAxiosError(error) &&
      error.response &&
      error.response.status &&
      error.response.status === 400
    ) {
      throw new Error('Invalid credentials, Please check.');
    }

    super.handleError(error);
  }

  async getFlowCabinets(export_url: string, token: string): Promise<any> {
    const url = export_url.replace(/\/+$/, '') + '/api/v4/cabinets/';
    return super.get(url, {
      headers: {
        Authorization: 'Token ' + token,
      },
    });
  }

  async getFlowDocumentTypes(export_url: string, token: string): Promise<any> {
    const url = export_url.replace(/\/+$/, '') + '/api/v4/document_types/';
    return super.get(url, {
      headers: {
        Authorization: 'Token ' + token,
      },
    });
  }

  public saveFlow2Config(
    configuration: Flow2NewExportConfiguration | Flow2EditExportConfiguration,
  ): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(configuration)) {
      formData.append(key, value);
    }
    return super.post(api.export_flow2, formData);
  }

  public saveSFTPConfig(configuration: SFTPExportConfiguration): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(configuration)) {
      formData.append(key, value);
    }
    return super.post(api.sftp_export, formData);
  }

  public saveInforSFTPConfig(
    configuration: InforSFTPExportConfiguration,
  ): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(configuration)) {
      formData.append(key, value);
    }
    return super.post(api.infor_sftp_export, formData);
  }

  public saveSMBConfig(configuration: SMBExportConfiguration): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(configuration)) {
      formData.append(key, value);
    }
    return super.post(api.smb_export, formData);
  }

  /**
   * Save a regular watcher export configuration (export_method='watcher').
   * This is different from Watchdog configurations which use the watchdog API.
   * @param configuration - Configuration with config_id, doc_type, title, export_folder, etc.
   */
  public saveWatcherExportConfig(configuration: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(configuration)) {
      if (value !== null && value !== undefined) {
        formData.append(key, String(value));
      }
    }
    return super.post(api.watcher_export, formData);
  }

  public testSFTP(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.test_sftp_connection, formData);
  }

  public testSMB(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.test_smb_connection, formData);
  }

  public testINFOR(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.test_infor_connection, formData);
  }

  public getExportConfigurationMappingSamples(
    mappingType: string,
  ): Promise<any> {
    const formData = new FormData();
    formData.append('type', mappingType);

    return super.post(api.get_export_configuration_mapping_samples, formData);
  }

  public duplicateTomlMappingSamples(formData: FormData): Promise<any> {
    return super.post(api.save_export_configuration_mapping_sample, formData);
  }

  public deleteExportConfigurationMappingSample(mappingId: string): Promise<any> {
    return super.delete(
      api.delete_export_configuration_mapping_sample + mappingId,
      {},
    );
  }

  public async fetchExportHistory(): Promise<any> {
    return super.get(api.fetch_export_history);
  }

  /**
   * Get all Watchdog configurations for the current organization.
   * Filters by config_type if needed (import/export).
   */
  public async getWatchdogConfigurations(): Promise<any> {
    return super.get(api.watchdog_configurations);
  }

  /**
   * Create a new Watchdog configuration.
   * @param config - Configuration object with name, config_type, enabled, etc.
   */
  public async saveWatchdogConfiguration(config: any): Promise<any> {
    return super.post(api.watchdog_configurations, config);
  }

  /**
   * Update an existing Watchdog configuration.
   * @param id - The configuration ID
   * @param config - Updated configuration object
   */
  public async updateWatchdogConfiguration(
    id: string,
    config: any,
  ): Promise<any> {
    return super.put(`${api.watchdog_configurations}/${id}`, config);
  }

  /**
   * Delete a Watchdog configuration.
   * @param id - The configuration ID to delete
   */
  public async deleteWatchdogConfiguration(id: string): Promise<any> {
    return super.delete(`${api.watchdog_configurations}/${id}`, {});
  }

  /**
   * Get all XSLT templates for Watchdog.
   */
  public async getWatchdogTemplates(): Promise<any> {
    return super.get(api.watchdog_templates);
  }

  /**
   * Create/upload a new XSLT template for Watchdog.
   * @param file - The XSLT file to upload
   */
  public async createWatchdogTemplate(file: File): Promise<any> {
    // Read file content and send as JSON (backend expects JSON, not FormData)
    const content = await file.text();
    // Remove .xslt or .xsl extension from filename for template name
    const name = file.name.replace(/\.(xslt?|xsl)$/i, '');
    return super.post(api.watchdog_templates, { name, content });
  }

  /**
   * Get export execution logs for a document.
   * @param docId - The document ID
   * @param options - Query options
   */
  public async getExportLogs(
    docId: string,
    options: {
      includeSkipped?: boolean;
      includeDeleted?: boolean;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<any> {
    const params = new URLSearchParams();
    params.append('include_skipped', String(options.includeSkipped ?? true));
    params.append('include_deleted', String(options.includeDeleted ?? false));
    params.append('limit', String(options.limit ?? 100));
    params.append('offset', String(options.offset ?? 0));

    return super.get(`${api.export_logs}${docId}?${params.toString()}`);
  }
}
